import StepType from "../flow/enums/stepType.enum"
import { Flow } from "../flow/flow.component"
import { useState, useEffect, useRef, useContext } from "react"
import { RosuvastatinConfirm } from "./rosuvastatinConfirm.component"
import { listMembershipTypes } from "../../services/membershipType.service"
import MembershipTypeCode from "../../enums/membershipTypeCode.enum"
import { completeFlow } from "../../services/flow.service"
import { UserContext } from "../../contexts/user.context"
import ProductHelper from "../../helpers/product.helper"
import Gender from "../../enums/gender.enum"
import TestCode from "../../enums/testCode.enum"
import FlowType from "../../enums/flowType.enum"
import MedicalCondition from "../../enums/medicalCondition.enum"
import FieldType from "../../enums/fieldType.enum"
import PatientHelper from "../../helpers/patient.helper"
import Disqualification from "../../enums/disqualification.enum"
import { BiomarkerPopover } from "../biomarkerPopover/biomarkerPopover.component"
import FileTypeCode from "../../enums/fileTypeCode.enum"
import { listResults } from "../../services/result.service"
import { listTests } from "../../services/test.service"

const disqualifyingMedicalConditions = [
  MedicalCondition.CIRROHOSIS,
  MedicalCondition.END_STAGE_LIVER_DISEASE,
  MedicalCondition.END_STAGE_KIDNEY_DISEASE,
]

export const Step = {
  INTRO: 'intro',
  REASON: 'reason',
  GENDER: 'gender',
  DOB: 'dob',
  MEDICAL_CONDITIONS: 'medical-conditions',
  PREGNANCY: 'pregnancy',
  BREASTFEEDING: 'breastfeeding',
  PRIOR_STATIN_USE: 'prior-statin-use',
  ADVERSE_EFFECTS: 'adverse-effects',
  LIPID_TEST: 'lipid-test',
  TEST_VALUES: 'test-values',

  // qualified or disqualified
  HEART_HEALTH_TEST: 'heart-health-test',
  QUALIFIED: 'qualified',
  DISQUALIFIED: 'disqualified',

  // payment steps 
  SELECT_DURATION: 'select-duration',
  PAY: 'pay',
  NAME: 'name',
  CONTACT: 'contact',
  GOV_ID: 'gov-id',
  DELIVERY: 'delivery',
  ADDITIONAL_INFO: 'additional-info',
  PASSWORD: 'password',
  CONFIRM: 'confirm',
}

export const RosuvastatinFlow = () => {
  const [flow, setFlow] = useState()
  const [skeleton, setSkeleton] = useState()
  const [membershipTypes, setMembershipTypes] = useState()
  const [hasPass, setHasPass] = useState()
  const hasCompletedFlowRef = useRef(false)
  const [initialUser, setInitialUser] = useState()
  const { instalabMembership, currentUser } = useContext(UserContext)
  const [hasTest, setHasTest] = useState(false)
  const [disqualifications, setDisqualifications] = useState([])
  
  useEffect(() => {
    const fetchMembershipTypes = async () => {
      const response = await listMembershipTypes({
        filter: {
          code: {
            $in: [
              MembershipTypeCode.ROSUVASTATIN_5MG_1MO,
              MembershipTypeCode.ROSUVASTATIN_5MG_3MO,
              MembershipTypeCode.ROSUVASTATIN_5MG_12MO,
            ]
          }
        }
      })
      setMembershipTypes(response)
    }
  
    fetchMembershipTypes()
  }, [])

  useEffect(() => {
    // don't show add on if has already run apoe
    const fetchHasTest = async () => {
      const tests = await listTests({
        select: '_id',
        filter: { code: { $in: [TestCode.DIRECT_LDL, TestCode.APO_B, TestCode.LDL_CHOLESTEROL_CALC] } }
      });
  
      const results = await listResults({
        select: '_id values',
        filter: { patient: currentUser }
      });
  
      const hasTest = results.some(({ values }) =>
        values.some(value => value.value && tests.some(test => test._id.toString() === value.test.toString()))
      );
  
      setHasTest(hasTest);
    };
  
    fetchHasTest();
  }, [currentUser]);

  useEffect(() => {
    fetchSkeleton()
  }, [membershipTypes, flow, hasPass, initialUser, instalabMembership, currentUser, hasTest])

  const getMembershipType = (code) => {
    if (!code) return null
    return membershipTypes?.find(membershipType => membershipType.code === code)
  }

  const fetchSkeleton = () => {
    if (hasPass === undefined || !initialUser  || !membershipTypes?.length) return

    setSkeleton({

      [Step.INTRO]: {
        type: StepType.STATIC,
        title: `First, see if rosuvastatin is a fit for you (takes just 2 minutes).`,
        description: `We’ll ask a few quick questions about your health and recent lab work. Your answers help us determine right away whether we can prescribe rosuvastatin (5 mg) to you.`,
        nextStep: Step.REASON,
        buttonText: "Let's get started!",
      },
      [Step.REASON]: {
        type: StepType.MULTIPLE_SELECT,
        nextStep: Step.DOB,
        buildUser: true,
        title: `Why are you interested in Rosuvastatin (5 mg)?`,
        description: 'Select all that apply.',
        field: {
          name: 'cholesterolReason',
          required: true,
          options: [ 
          {
            label: `No doctor to help me manage my cholesterol`,
            value: `No doctor to help me manage my cholesterol`,
          }, {
            label: `Lower my risk of heart attack or stroke`,
            value: `Lower my risk of heart attack or stroke`,
          }, 
          {
            label: 'Have high LDL or ApoB and want to bring it down',
            value: 'Have high LDL or ApoB and want to bring it down',
          },
          {
            label: "Have family history of heart disease",
            value: "Have family history of heart disease",
          },
          {
            label: `Had side effects from higher-dose statins`,
            value: `Had side effects from higher-dose statins`,
          },
          {
            label: 'Other',
            value: 'Other',
          }]
        },
      },
      [Step.DOB]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.GENDER,
        buildUser: true,
        title: `What's your date of birth?`,
        description: `Enter in the format of MM/DD/YYYY.`,
        field: {
          name: 'dob',
          placeholder: 'MM/DD/YYYY',
          date: true,
          inputMode: 'numeric',
          required: true,
        },
        skipIf: () => {
          return initialUser?.dob
        }
      },

      [Step.GENDER]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.MEDICAL_CONDITIONS,
        buildUser: true,
        title: `What's your biological sex?`,
        field: {
          name: 'gender',
          required: true,
          options: [{
            label: 'Male',
            value: Gender.MALE,
          }, {
            label: 'Female',
            value: Gender.FEMALE,
          }]
        },
        skipIf: () => {
          return initialUser?.gender
        }
      },

      [Step.MEDICAL_CONDITIONS]: {
        type: StepType.MULTIPLE_SELECT,
        onNextStep: (patient) => {
          if (patient?.gender === Gender.FEMALE) {
            return Step.PREGNANCY
          } else {
            return Step.PRIOR_STATIN_USE
          }
        },
        buildUser: true,
        title: `Have you been diagnosed with any of the following conditions?`,
        description: 'Select all that apply.',
        field: {
          name: 'currentMedicalConditions',
          options: disqualifyingMedicalConditions.map(condition => ({
            label: condition,
            value: condition,
          })),
        },
      },

      [Step.PREGNANCY]: {
        type: StepType.SINGLE_SELECT,
        onNextStep: (patient) => {
          if (patient?.isPregnant) {
            return Step.PRIOR_STATIN_USE
          } else {
            return Step.BREASTFEEDING
          }
        },
        buildUser: true,
        title: `Are you pregnant or planning to be in the next 12 months?`,
        field: {
          name: 'isPregnant',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },
      [Step.BREASTFEEDING]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.PRIOR_STATIN_USE,
        buildUser: true,
        title: `Are you currently breastfeeding?`,
        field: {
          name: 'isBreastfeeding',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },

      [Step.PRIOR_STATIN_USE]: {
        type: StepType.SINGLE_SELECT,
        onNextStep: (patient) => {
          if (patient?.hasPriorStatinUse) {
            return Step.ADVERSE_EFFECTS
          } else {
            return Step.LIPID_TEST
          }
        },
        buildUser: true,
        title: `Have you taken a statin before?`,
        field: {  
          name: 'hasPriorStatinUse',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
      },

      [Step.ADVERSE_EFFECTS]: {
        type: StepType.SINGLE_SELECT,
        nextStep: Step.LIPID_TEST,
        buildUser: true,
        title: `Have you had any adverse reactions to statins before?`,
        field: {  
          name: 'cholesterolAdverseReaction.statins',
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        }
      },

      [Step.LIPID_TEST]: {
        type: StepType.SINGLE_SELECT,
        buildUser: true,
        title: `Have you had blood work done in the past year that includes LDL cholesterol or ApoB?`,
        field: {
          name: 'hasLipidTest',
          required: true,
          options: [{
            label: 'Yes',
            value: true,
          }, {
            label: 'No',
            value: false,
          }]
        },
        onNextStep: (patient) => {
          if (patient?.hasLipidTest || hasTest) {
            return Step.TEST_VALUES
          } 
          else  {
            return Step.HEART_HEALTH_TEST
          }
        },
        skipIf: () => hasTest
      },

      [Step.TEST_VALUES]: {
        type: StepType.MULTIPLE_INPUT,
        onNextStep: (patient) => {

          const result = PatientHelper.statinQualified(patient)
          const { qualified, disqualifications } = result
          
          if (qualified) {
            return Step.QUALIFIED
          }
          else {
            setDisqualifications(disqualifications)
            return Step.DISQUALIFIED
          }
        },        
        buildUser: true,
        title: `Enter your latest ApoB or LDL cholesterol result.`,
        requiresOneField: true,
        fields: [{
          name: 'ldl',
          label: 'LDL Cholesterol',
          number: true,
          inputMode: 'decimal',
          placeholder: '100',
          suffixField: {
            name: 'ldlUnit',
            default: 'mg/dL',
            options: [{
              label: 'mg/dL',
              value: 'mg/dL',
            }, {
              label: 'mmol/L',
              value: 'mmol/L',
            }]
          }
        }, {
          name: 'apob',
          label: 'ApoB',
          number: true,
          inputMode: 'decimal',
          placeholder: '90',
          suffixField: {
            name: 'apobUnit',
            default: 'mg/dL',
            options: [{
              label: 'mg/dL',
              value: 'mg/dL',
            }, {
              label: 'mmol/L',
              value: 'mmol/L',
            }]
          }
        }],
        skipIf: () => hasTest
      },

      [Step.HEART_HEALTH_TEST]: {
        type: StepType.STATIC,
        title: `Please, first take a blood test to get prescription.`,
        description: <>Currently, we lack the required medical data to proceed. You'll need to get a blood test for ApoB or LDL cholesterol to move forward. We can help you with this with our <BiomarkerPopover trigger={<a>Advanced Heart Health Test</a>}/>. Click the button below to get started.</>,
        buttonText: 'Get Tested',
        path: '/heart-health-test',
      },


      [Step.QUALIFIED]: {
        type: StepType.STATIC,
        title: `You’re approved for Rosuvastatin (5 mg)!`,
        description: `Just a few quick questions left before we can send your prescription.`,
        buttonText: 'Continue',
        nextStep: Step.SELECT_DURATION,
      },

      [Step.DISQUALIFIED]: {
        type: StepType.STATIC,
        title: `Apologies, we can't prescribe Rosuvastatin (5mg) to you at this time.`,
        description: () => {

          let text = ''
          switch (disqualifications?.[0]) {
            case Disqualification.PREGNANT:
              text = <>
                <p>Rosuvastatin isn’t safe to use during pregnancy because it can interfere with your baby’s development. Cholesterol is essential for building your baby’s cells and hormones, so we’ll need to stop this medication right away if you’re pregnant or planning to become pregnant.</p>
                <p>But you’re not on your own - we offer a physician-guided <a href="/heart">Heart Health Program</a> that takes your pregnancy into account and helps you manage your cholesterol through a personalized, safe approach. This includes non-drug strategies and, if needed, pregnancy-safe treatments to protect both you and your baby.</p>
              </>
              break
            case Disqualification.BREASTFEEDING:
              text = <>
                <p> Since you’re currently breastfeeding, rosuvastatin isn’t recommended because it can pass into your breast milk and may affect your baby.</p>
                <p>That said, you still deserve excellent heart care. Our <a href="/heart">Heart Health Program</a> offers a personalized plan tailored for nursing mothers, with physician oversight and alternative options for managing your cholesterol. We’ll work with you to protect your long-term heart health while keeping your baby safe.</p>
              </>
              break
            case Disqualification.STATIN_ADVERSE_REACTION:
              text = <>
                <p>If you’ve had a serious reaction to a statin, such as severe muscle pain, liver issues, or allergic symptoms, we need to be extra cautious. You may benefit from a different statin at a lower dose, adjusted timing, or an alternative cholesterol-lowering medication altogether. </p>
                <p>Our physician-guided <a href="/heart">Heart Health Program</a> is designed for situations just like this. We’ll closely review your history, monitor you carefully, and tailor a treatment plan that’s safe, effective, and built around your individual needs. You won’t have to navigate it alone—we’ll guide you every step of the way.</p>
              </>
              break
            case Disqualification.END_STAGE_KIDNEY_DISEASE:
              text = <>
                <p>Rosuvastatin isn’t usually started in patients with end stage kidney disease because studies show it doesn’t offer much heart protection at this stage and may increase the risk of muscle damage.</p>
                <p>However, every person’s situation is unique. Through our <a href="/heart">Heart Health Program</a>, we offer a comprehensive review of your condition and create a plan that works for your kidney health and cardiovascular needs. Our physician-led team will help you explore alternatives that are both effective and safe for you.</p>
              </>
              break
            case Disqualification.END_STAGE_LIVER_DISEASE:
            case Disqualification.CIRROHOSIS:
              text = <>
                <p>If your liver is severely damaged, rosuvastatin isn’t a safe option because your body can’t process it properly. This could lead to more harm than benefit.</p>
                <p>But you still deserve proactive care. Our <a href="/heart">Heart Health Program</a> gives you access to a personalized plan developed by a physician who will consider your liver status and help you explore the safest options for lowering your cholesterol, including lifestyle approaches and liver-friendly alternatives.</p>
              </>
              break
            case Disqualification.UNDER_18:
              text = <>Rosuvastatin isn’t typically used in people under 18 unless there’s a rare inherited cholesterol condition. For most young people, the first step is building a foundation of healthy habits like better nutrition and regular physical activity. If your cholesterol remains very high, our <a href="/heart">Heart Health Program</a> offers expert guidance tailored for younger patients. A physician will work with you and your family to create a safe, long-term plan - whether that includes medication or not.</>
              break

            default:
              text = null
              break;
          }

          return <>
            {text ? <p>{text}</p> : <p>Based on your answers, we do not recommend statin therapy at this time. Please contact us if you disagree or have any questions. We're happy to consider situations on a case-by-case basis.</p>}
            
          </>
        },
        buttonText: 'Go to Heart Health Program',
        enterStyle: { display: 'none' },
        path: '/heart',


      },


    
      [Step.SELECT_DURATION]: {
        type: StepType.PRODUCT_SELECT,
        title: `How much Rosuvastatin (5mg) would you like to order?`,
        description: "Pick the option that fits your goals. Each includes automatic refills. Cancellation is easy and available anytime before renewal.",
        nextStep: Step.DELIVERY,
        buildFlow: true,
        field: {
          name: 'membershipTypeCode',
          required: true,
          options: [
            {
              title: '30 tablets',
              value: MembershipTypeCode.ROSUVASTATIN_5MG_1MO,
              price: `$${getMembershipType(MembershipTypeCode.ROSUVASTATIN_5MG_1MO)?.cost}/month`,
              description: `Refilled & billed monthly at $${getMembershipType(MembershipTypeCode.ROSUVASTATIN_5MG_1MO)?.cost} — best if just assessing tolerance.`,
            },
            {
              title: '90 tablets',
              value: MembershipTypeCode.ROSUVASTATIN_5MG_3MO,
              price: `$${getMembershipType(MembershipTypeCode.ROSUVASTATIN_5MG_3MO)?.cost/3}/month`,
              description: `Refilled & billed quarterly at $${getMembershipType(MembershipTypeCode.ROSUVASTATIN_5MG_3MO)?.cost} — ideal for first-time users to evaluate response and initial labs.`,
            },
            {
              title: '365 tablets',
              value: MembershipTypeCode.ROSUVASTATIN_5MG_12MO,
              price: `$${getMembershipType(MembershipTypeCode.ROSUVASTATIN_5MG_12MO)?.cost/12}/month`,
              description: `Refilled & billed annually at $${getMembershipType(MembershipTypeCode.ROSUVASTATIN_5MG_12MO)?.cost} — best value for long-term use.`,
            },

          ],
        },

      },

      [Step.DELIVERY]: {
        type: StepType.LOCATION,
        buildUser: true,
        nextStep: Step.PAY,
        title: `What's your delivery address?`,
        description: `We'll send your medication to this location.`,
        field: {
          name: 'shippingLocation'
        },
        
      },

      [Step.PAY]: {
        type: StepType.PAY,
        nextStep: Step.NAME,
        addSubscription: true,
        paymentTypes: ['card'],
        subscriptionType: flow?.membershipTypeCode,
        title: () => { 
          const selectedMembershipType = getMembershipType(flow?.membershipTypeCode);
          const totalCost = ProductHelper.getTotalCost([selectedMembershipType], instalabMembership, currentUser)
          
          const isAnnual = selectedMembershipType?.isAnnual
          const isQuarterly = selectedMembershipType?.isQuarterly
          const isMonthly = selectedMembershipType?.isMonthly
          
          return <>Pay <span className="true-price">${totalCost}</span> for your {isAnnual ? '365' : isQuarterly ? '90' : isMonthly ? '30' : ''}  tablets of {selectedMembershipType?.title}</>
        },
        description: () => {

          const selectedMembershipType = getMembershipType(flow?.membershipTypeCode);
          const isAnnual = selectedMembershipType?.isAnnual
          const isQuarterly = selectedMembershipType?.isQuarterly
          const isMonthly = selectedMembershipType?.isMonthly
          
          return <>You'll be refilled and billed {isAnnual ? 'annually' : isQuarterly ? 'every 3 months' : isMonthly ? 'monthly' : ''} this amount. If plans change, cancellation is very easy and always available before renewal.</>
        },
       
      },

      [Step.NAME]: {
        type: StepType.MULTIPLE_INPUT,
        nextStep: Step.CONTACT,
        buildUser: true,
        title: `What's your full name?`,
        description: `We need this for your prescription.`,
        fields: [{
          name: 'firstName',
          placeholder: 'First Name',
          required: true,
        }, {
          name: 'lastName',
          placeholder: 'Last Name',
          required: true,
        }],
        skipIf: () => {
          return initialUser?.firstName && initialUser?.lastName
        }
      },

      [Step.CONTACT]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.GOV_ID,
        buildUser: true,
        title: `How can we reach you?`,
        description: "We’ll send your tracking information for your medication here.",
        fields: [
          {
          name: 'email',
          label: 'Email Address',
          required: true,
          type: 'email',
          unique: true,
        }, {
          name: 'phone',
          label: 'Phone Number',
          required: true,
          type: 'phone',
        }],
        skipIf: () => {
          return (initialUser?.email && initialUser?.phone)
        }
      },



      [Step.GOV_ID]: {
        type: StepType.INSURANCE_UPLOAD,
        buildUser: true,
        nextStep: Step.ADDITIONAL_INFO,
        title: 'Upload your government-issued ID',
        description: `To keep you safe and stay compliant with the law, we need to verify your identity before we can send your medication. Accepted IDs include a passport, driver’s license, or state ID.`,
        fields: [
          {
            name: 'govId',
            fileTypeCode: FileTypeCode.GOV_ID,
            upload: true,
            title: 'Upload your ID securely here',
            required: true,
          },
    
        ],
    
        skipIf: () => {
          return initialUser?.govId
        }
      },


      [Step.ADDITIONAL_INFO]: {
        type: StepType.MULTIPLE_FIELDS,
        nextStep: Step.PASSWORD,
        buildUser: true,
        buttonText: 'Finish',
        title: `One last step to finalize your order.`,
        description: `We just need a few last details to ensure we give you the right instructions on how to safely take your medication.`,
        fields: [

          {
            type: FieldType.TEXTAREA,
            name: 'freeformMedications',
            placeholder: 'Type medications here ...',
            label: <span className="highlight-label">List any medications you currently take.</span>,
          },   
          {
            type: FieldType.TEXTAREA,
            name: 'freeformSupplements',
            placeholder: 'Type supplements here ...',
            label: <span className="highlight-label">List any vitamins or supplements you currently take.</span>,
          },     
  

        ],
      },

      

      [Step.PASSWORD]: {
        type: StepType.SINGLE_INPUT,
        nextStep: Step.CONFIRM,
        buildUser: true,
        title: `Finally, create a password for your Instalab account.`,
        description: `This lets you securely access your prescription and track your health over time.`,
        field: {
          name: 'password',
          placeholder: 'Enter your password',
          required: true,
          password: true,
        },
        skipIf: () => {
          return hasPass
        },
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        }
      },

  
      [Step.CONFIRM]: {
        type: StepType.STATIC,
        title: `All set! `,
        path: `/flow/${FlowType.INTAKE}`,
        description: <>We'll email you in 1 business day with tracking information for your medication. If you have some time, please hit 'Continue' to tell us more about yourself so we can help make better recommendations on your health to you.</>,
        buttonText: 'Continue',
        content: <RosuvastatinConfirm flow={flow} />,
        onLoad: async () => {
          if (flow?._id && !hasCompletedFlowRef.current) {
            hasCompletedFlowRef.current = true; // Mark flow as completed
            await completeFlow(flow._id);
          }
        },

      },
    })
  }


  return (
    <Flow 
      skeleton={skeleton} 
      flow={flow} 
      setFlow={setFlow}
      initialUser={initialUser}
      setInitialUser={setInitialUser}
      setHasPass={setHasPass}
    />
  )
}